import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Button,
  Spinner,
  Table,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
} from "reactstrap";

import api from "../../../services/api";
import { rotuloDocumento } from "../../../utils/modeloDocumento";
import CteAuditDetailModal from "../../../components/cte/CteAuditDetailModal";
import CtesValidacao from "./CtesValidacao";
import { useCompanies } from "../../../hooks/companies";
import Select from "react-select";
import {
  fmtBRL,
  fmtInt,
  fmtData,
  fmtDataPura,
  fmtDataBRT,
  dataPura,
  fmtDataHora,
  labelSituacao,
  ACOES_CONCILIACAO,
  MOTIVOS_APROVACAO,
  OBSERVACAO_MAX,
  rotuloAcao,
  enviaFaturaPorEmail,
  validarConciliacao,
  montarCorpoConciliacao,
  montarCorpoLote,
  labelResponsavel,
} from "./formatters";
import AnexosCampo from "./AnexosCampo";
import LinhaDoTempoConciliacao from "./LinhaDoTempoConciliacao";
import { StatusBadge } from "./componentesCompartilhados";
import { montarLinhaDoTempo } from "./historicoConciliacao";
import { makeReactSelectStyles, reactSelectPortalProps } from "../../../utils/reactSelectStyles";
import {
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotTitleStyle,
} from "../../../components/autopilot/styles";

// Menu em portal acima do modal do Bootstrap (z-index 1050); o padrão (50) abria atrás dele.
const selectNoModalStyles = makeReactSelectStyles({ portalZIndex: 1070 });

const STATUS_TITULO = {
  OOK: "Documentos OK",
  NOK: "Documentos Não OK",
  AVA: "Documentos A validar",
};

// Hook simples de fetch on-open para os modais de drill-down.
function useDrilldown(isOpen, url) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);

  useEffect(() => {
    if (!isOpen || !url) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(url)
      .then((res) => {
        if (!cancelled) setData(Array.isArray(res.data) ? res.data : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setData([]);
        setError(
          (e && e.response && e.response.data && e.response.data.error) ||
            e.message ||
            "Erro ao carregar"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, url]);

  // setData: ajuste local de uma linha sem recarregar a lista (e sem perder as
  // marcações OK/NOK ainda não salvas), ex.: simulado novo após recálculo (PO1-3197).
  return { loading, error, data, setData };
}

function ModalShell({ isOpen, onClose, titulo, subtitulo, variant, children }) {
  const shellStyle =
    variant === "detail"
      ? { ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)" }
      : autopilotCardStyle;
  const variantClass = variant ? ` conf-fatura-modal-shell--${variant}` : "";

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="xl"
      modalClassName="modal-align-top"
      fade={false}
    >
      <div className={`conf-fatura-modal-shell${variantClass}`} style={shellStyle}>
        <div className="conf-fatura-modal-header" style={autopilotHeaderStyle}>
          <div>
            <div style={autopilotEyebrowStyle}>Auditoria de Faturas</div>
            <div style={{ ...autopilotTitleStyle, fontSize: "1rem", marginBottom: "0.2rem" }}>
              {titulo}
            </div>
            {subtitulo && <div style={autopilotDescriptionStyle}>{subtitulo}</div>}
          </div>
          <Button close onClick={onClose} />
        </div>
        <div className="conf-fatura-modal-body" style={autopilotBodyStyle}>
          {children}
        </div>
      </div>
    </Modal>
  );
}

function EstadoModal({ loading, error, vazio }) {
  if (loading)
    return (
      <div className="conf-fatura-loading">
        <Spinner color="info" />
      </div>
    );
  if (error)
    return (
      <div className="conf-fatura-empty">
        <strong>Erro:</strong> {typeof error === "object" ? JSON.stringify(error) : error}
      </div>
    );
  if (vazio) return <div className="conf-fatura-empty">Nenhum item encontrado.</div>;
  return null;
}

// PO1-3191: a marcação manual de CT-e vale em Em Análise, Em Tratativa e
// Contestada (o backend recusa nas demais). Ela não muda a situação da fatura:
// quem valida é "Validar agora"/"Revalidar" ou "Aprovar com justificativa".
export const SITUACOES_EDITAVEIS = ["EAN", "ETR", "CON"];

// Depois de recalcular frete de CT-e da fatura: o robô aplica a regra de
// marcação OK/NOK e, em Em Análise com pendência só de frete, reconfere a
// fatura (pode sair para Validada/Liberada). Devolve a resposta do robô.
export async function remarcarCtesDaFatura(idFatura) {
  const res = await api.post(`/faturas/conferencia/${idFatura}/ctes/remarcar`);
  return (res && res.data) || {};
}

// Situação nova quando a reconferência mudou a fatura, ou null.
export const situacaoReconferida = (resposta) => {
  const r = resposta && resposta.reconferencia;
  return r && r.status === "ok" && r.situacaoNova ? r.situacaoNova : null;
};

// O que a fatura precisa depois do recálculo, conforme a situação.
export const proximoPassoAposRecalculo = (situacaoAtual, resposta) => {
  const nova = situacaoReconferida(resposta);
  if (nova) return `A fatura foi reconferida e passou para ${labelSituacao(nova)}.`;
  if (situacaoAtual === "ETR") return "Clique em Revalidar para a fatura seguir.";
  if (situacaoAtual === "EAN") return "A fatura continua Em Análise: ainda há pendência na conferência.";
  return "";
};

// Detalhe do CT-e (Auditoria de CT-e: components/cte/CteAuditDetailModal),
// alimentado por /dashboard/auditoria/cte/details/:id. Usado pela lista de CT-e
// da fatura e pelas pendências da lateral (PO1-2256). `abrir` recebe
// { id, numero } com o Conhecimentotransporte.Id.
export function useDetalheCte({ onRecalculado } = {}) {
  const { empresa } = useCompanies();
  const empresaId = empresa && empresa.value;
  const [cteRow, setCteRow] = useState(null);
  const [cteDetails, setCteDetails] = useState(null);
  const [cteLoading, setCteLoading] = useState(false);
  const [cteError, setCteError] = useState("");

  const mensagem = (e, padrao) =>
    (e && e.response && e.response.data && e.response.data.error) || (e && e.message) || padrao;

  const abrir = async (c) => {
    if (!c || !c.id) return;
    setCteRow({ numeroCTe: c.numero, idCTeValue: c.id });
    setCteDetails(null);
    setCteError("");
    setCteLoading(true);
    try {
      const response = await api.get(`/dashboard/auditoria/cte/details/${c.id}`, {
        params: { embarcadorId: empresaId },
      });
      setCteDetails(response.data || null);
    } catch (e) {
      setCteError(mensagem(e, "Não foi possível carregar os dados do CT-e."));
    } finally {
      setCteLoading(false);
    }
  };

  // Depois do cálculo/recálculo no detalhe: recarrega sem mostrar o carregando
  // (o botão e a mensagem do resultado continuam na tela).
  const recarregar = async () => {
    if (!cteRow || !cteRow.idCTeValue) return;
    try {
      const response = await api.get(`/dashboard/auditoria/cte/details/${cteRow.idCTeValue}`, {
        params: { embarcadorId: empresaId },
      });
      const novo = response.data || null;
      setCteDetails(novo);
      if (novo && novo.summary && onRecalculado) await onRecalculado(cteRow.idCTeValue, novo.summary);
    } catch (e) {
      setCteError(mensagem(e, "Não foi possível recarregar os dados do CT-e."));
    }
  };

  const fechar = () => {
    setCteRow(null);
    setCteDetails(null);
    setCteError("");
    setCteLoading(false);
  };

  const elemento = (
    <CteAuditDetailModal
      isOpen={Boolean(cteRow)}
      row={cteRow}
      detail={cteDetails}
      loading={cteLoading}
      error={cteError}
      onClose={fechar}
      onRecalculated={recarregar}
    />
  );

  return { abrir, elemento };
}

export function CtesModal({ isOpen, onClose, fatura, status, onSaved }) {
  const id = fatura && fatura.id;
  const url =
    isOpen && id
      ? `/faturas/conferencia/${id}/ctes${status ? `?status=${status}` : ""}`
      : null;
  const { loading, error, data, setData } = useDrilldown(isOpen, url);
  // Lista atual para quem roda fora do ciclo de render (acompanhamento do recálculo).
  const dataRef = useRef(data);
  dataRef.current = data;

  // Edição (H4): só quando a fatura está em situação que permite validar CT-e.
  const editavel = fatura && SITUACOES_EDITAVEIS.includes(fatura.situacaoCodigo);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Detalhe do CT-e (PO1-3197): o mesmo modal da Auditoria de CT-e, pelo hook
  // compartilhado com as pendências da lateral. Depois de recalcular no detalhe,
  // atualiza só a linha do CT-e na lista, preservando as marcações OK/NOK.
  const { empresa } = useCompanies();
  const empresaId = empresa && empresa.value;
  const detalheCte = useDetalheCte({
    onRecalculado: async (idCte, resumo) => {
      setData((linhas) =>
        linhas.map((linha) =>
          String(linha.id) === String(idCte)
            ? { ...linha, freteSimulado: resumo.valorFreteSimulado, divergencia: resumo.staDivergencia }
            : linha
        )
      );
      await remarcarAposDetalhe();
    },
  });

  // PO1-2256: recalcular o frete de vários CT-e de uma vez (todos ou os
  // selecionados). Enfileira pela rota de lote do recálculo (mesma fila de
  // prioridade do Recalcular Frete) e acompanha a execução; ao terminar, atualiza
  // simulado e divergência das linhas sem perder as marcações OK/NOK não salvas.
  const [recalculo, setRecalculo] = useState(null); // { executionId, executando, tom, texto }
  const FIM_EXECUCAO = ["completed", "completed_with_errors", "completed_with_business_errors"];

  useEffect(() => {
    if (!isOpen) setRecalculo(null);
  }, [isOpen, id]);

  // PO1-2256: depois do recálculo, aplica a regra de marcação da conferência
  // (OK sem divergência, Não OK divergente, sem cálculo não mexe) pelo robô,
  // sem mudar a situação da fatura, e recarrega a lista. Devolve quantos CT-e
  // mudaram para OK e para Não OK, ou { erro }.
  const remarcarERecarregar = async () => {
    if (!url || !id) return { paraOk: 0, paraNok: 0 };
    const antes = new Map(dataRef.current.map((c) => [String(c.id), c.situacaoCodigo]));
    let erroRemarcar = null;
    let resposta = null;
    try {
      resposta = await remarcarCtesDaFatura(id);
    } catch (e) {
      erroRemarcar =
        (e && e.response && e.response.data && e.response.data.error) ||
        e.message ||
        "não foi possível atualizar as marcações";
    }
    try {
      const res = await api.get(url);
      const novas = Array.isArray(res.data) ? res.data : [];
      const paraOk = novas.filter((n) => n.situacaoCodigo === "OOK" && antes.get(String(n.id)) !== "OOK").length;
      const paraNok = novas.filter((n) => n.situacaoCodigo === "NOK" && antes.get(String(n.id)) !== "NOK").length;
      setData(novas);
      const nova = situacaoReconferida(resposta);
      if (!erroRemarcar && onSaved) {
        onSaved(
          {
            qtdCteOk: novas.filter((n) => n.situacaoCodigo === "OOK").length,
            qtdCteNok: novas.filter((n) => n.situacaoCodigo === "NOK").length,
            ...(nova ? { situacaoCodigo: nova, situacaoNome: labelSituacao(nova) } : {}),
          },
          id
        );
      }
      return { paraOk, paraNok, erro: erroRemarcar, resposta };
    } catch (e) {
      return { paraOk: 0, paraNok: 0, erro: erroRemarcar || "não foi possível recarregar a lista", resposta };
    }
  };

  // Texto das marcações depois do recálculo (lote ou detalhe do CT-e).
  const textoMarcacao = (marcacao) => {
    const partes = [];
    if (marcacao.erro) {
      partes.push(`As marcações OK/NOK não foram atualizadas (${marcacao.erro}).`);
    } else if (marcacao.paraOk || marcacao.paraNok) {
      const mudou = [];
      if (marcacao.paraOk) mudou.push(`${fmtInt(marcacao.paraOk)} passaram para OK`);
      if (marcacao.paraNok) mudou.push(`${fmtInt(marcacao.paraNok)} passaram para NOK por divergência`);
      partes.push(`${mudou.join(" e ")}.`);
    } else {
      partes.push("As marcações OK/NOK não mudaram.");
    }
    if (!marcacao.erro) {
      const passo = proximoPassoAposRecalculo(fatura && fatura.situacaoCodigo, marcacao.resposta);
      if (passo) partes.push(passo);
    }
    return partes;
  };

  // Recalcular frete no detalhe de um CT-e (PO1-3197): mesma remarcação do lote,
  // senão a fatura em Em Análise só andava na próxima rodada do robô.
  const remarcarAposDetalhe = async () => {
    if (!editavel) return;
    const marcacao = await remarcarERecarregar();
    setRecalculo({
      executando: false,
      tom: marcacao.erro ? "aviso" : "ok",
      texto: ["Recálculo do CT-e concluído.", ...textoMarcacao(marcacao)].join(" "),
    });
  };

  const recalcularCtes = async (ids) => {
    const set = new Set((ids || []).map(String));
    const chaves = data.filter((c) => set.has(String(c.id)) && c.chave).map((c) => c.chave);
    if (!chaves.length || !empresaId) return;
    setRecalculo({ executando: true, tom: "info", texto: `Enviando ${fmtInt(chaves.length)} CT-e para recálculo…` });
    try {
      const res = await api.post("/recalculo/cte/por-chaves", { embarcadorId: empresaId, chaves });
      const executionId = res && res.data && res.data.executionId;
      if (!executionId) throw new Error("O recálculo não devolveu o identificador da execução.");
      setRecalculo({
        executionId,
        total: chaves.length,
        executando: true,
        tom: "info",
        texto: `Recalculando ${fmtInt(chaves.length)} CT-e…`,
      });
    } catch (e) {
      setRecalculo({
        executando: false,
        tom: "erro",
        texto:
          (e && e.response && e.response.data && e.response.data.error) ||
          e.message ||
          "Não foi possível enviar os CT-e para recálculo.",
      });
    }
  };

  const executionId = recalculo && recalculo.executando ? recalculo.executionId : null;
  useEffect(() => {
    if (!executionId) return undefined;
    let ativo = true;
    const consultar = async () => {
      try {
        const res = await api.get(`/recalculo/cte/executions/${executionId}`, {
          params: { embarcadorId: empresaId },
        });
        if (!ativo) return;
        const ex = res.data || {};
        const total = Number(ex.total) || 0;
        const processados = Number(ex.processed) || 0;
        if (!FIM_EXECUCAO.includes(ex.status)) {
          setRecalculo((r) =>
            r && r.executionId === executionId
              ? { ...r, texto: `Recalculando: ${fmtInt(processados)} de ${fmtInt(total)} CT-e…` }
              : r
          );
          return;
        }
        const contagem = ex.counts || {};
        const erros = (Number(contagem.businessErrors) || 0) + (Number(contagem.technicalErrors) || 0);
        const marcacao = await remarcarERecarregar();
        if (!ativo) return;
        const partes = [
          erros
            ? `Recálculo concluído: ${fmtInt(total - erros)} de ${fmtInt(total)} CT-e recalculados; ${fmtInt(erros)} não calcularam (bloqueados ou sem tabela).`
            : `Recálculo concluído: ${fmtInt(total)} CT-e recalculados.`,
        ];
        partes.push(...textoMarcacao(marcacao));
        setRecalculo({
          executando: false,
          tom: erros || marcacao.erro ? "aviso" : "ok",
          texto: partes.join(" "),
        });
      } catch (e) {
        if (!ativo) return;
        setRecalculo((r) =>
          r && r.executionId === executionId
            ? { ...r, texto: "Recalculando… (não foi possível consultar o andamento, tentando de novo)" }
            : r
        );
      }
    };
    consultar();
    const intervalo = window.setInterval(consultar, 4000);
    return () => {
      ativo = false;
      window.clearInterval(intervalo);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId, empresaId]);

  // Limpa as edições sempre que recarrega a lista.
  useEffect(() => {
    setEdits({});
    setSaveError(null);
  }, [data]);

  const valorAtual = (c) => edits[c.id] || c.situacaoCodigo;
  const marcar = (cteId, situacao) =>
    setEdits((prev) => ({ ...prev, [cteId]: situacao }));
  // PO1-3199: validação rápida e ações em massa. situacao null = limpar a marcação
  // (volta à situação gravada).
  const marcarVarios = (cteIds, situacao) =>
    setEdits((prev) => {
      const novo = { ...prev };
      (cteIds || []).forEach((cteId) => {
        if (situacao) novo[cteId] = situacao;
        else delete novo[cteId];
      });
      return novo;
    });

  const alterados = data
    .filter((c) => edits[c.id] && edits[c.id] !== c.situacaoCodigo)
    .map((c) => ({ idCte: c.id, situacao: edits[c.id] }));

  // PO1-3191: AVA deixou de ser editável (a passagem AVA → EVA por marcação
  // manual saiu), então só vai o que o usuário alterou.
  const paraSalvar = alterados;

  const handleSalvar = async () => {
    if (paraSalvar.length === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const response = await api.put(`/faturas/conferencia/${id}/ctes/validar`, {
        updates: paraSalvar,
      });
      if (onSaved) onSaved(response.data, id);
      onClose();
    } catch (e) {
      setSaveError(
        (e && e.response && e.response.data && e.response.data.error) ||
          e.message ||
          "Erro ao salvar validação"
      );
    } finally {
      setSaving(false);
    }
  };

  const titulo = status ? STATUS_TITULO[status] || "Documentos" : "Documentos da fatura";
  // PO1-3199: número externo da fatura (o que o usuário conhece) + interno.
  const subtitulo = fatura
    ? [
        fatura.numFaturaExterna ? `Fatura nº ${fatura.numFaturaExterna}` : `Fatura ${fatura.id}`,
        fatura.numFaturaExterna ? `Nº interno ${fatura.id}` : null,
        fatura.transportadoraFantasia || null,
        data.length ? `${fmtInt(data.length)} documento(s)` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <>
    <ModalShell isOpen={isOpen} onClose={onClose} titulo={titulo} subtitulo={subtitulo}>
      <EstadoModal loading={loading} error={error} vazio={data.length === 0} />
      {!loading && !error && data.length > 0 && (
        <CtesValidacao
          data={data}
          editavel={editavel}
          saving={saving}
          saveError={saveError}
          valorAtual={valorAtual}
          marcar={marcar}
          marcarVarios={marcarVarios}
          alterados={paraSalvar}
          onSalvar={handleSalvar}
          onAbrirCte={detalheCte.abrir}
          onRecalcular={recalcularCtes}
          recalculo={recalculo}
        />
      )}
    </ModalShell>
    {detalheCte.elemento}
    </>
  );
}

export function LancamentosModal({ isOpen, onClose, fatura }) {
  const id = fatura && fatura.id;
  const url = isOpen && id ? `/faturas/conferencia/${id}/lancamentos` : null;
  const { loading, error, data } = useDrilldown(isOpen, url);

  const subtitulo = fatura ? `Fatura ${fatura.id} · ${fatura.transportadoraFantasia || ""}` : "";

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      titulo="Lançamentos (Débito / Crédito)"
      subtitulo={subtitulo}
    >
      <EstadoModal loading={loading} error={error} vazio={data.length === 0} />
      {!loading && !error && data.length > 0 && (
        <div className="table-responsive">
          <Table className="conf-fatura-table" hover>
            <thead className="text-primary">
              <tr>
                <th>Tipo</th>
                <th className="num">Valor</th>
                <th>Descrição</th>
                <th>Nota Fiscal</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {data.map((l) => (
                <tr key={l.id}>
                  <td>{l.tipo === "D" ? "Débito" : l.tipo === "C" ? "Crédito" : l.tipo}</td>
                  <td className="num">{fmtBRL(l.valor)}</td>
                  <td>{l.descricao}</td>
                  <td>{l.notaFiscalNumero}</td>
                  <td>{fmtDataHora(l.data)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </ModalShell>
  );
}

const valorCampo = (valor) =>
  valor === null || valor === undefined || valor === "" ? "-" : valor;

function DetailMetric({ label, value, tone }) {
  return (
    <div className={`conf-fatura-detail-metric conf-fatura-detail-metric--${tone || "neutral"}`}>
      <span>{label}</span>
      <strong>{valorCampo(value)}</strong>
    </div>
  );
}

function DetailSection({ title, description, children }) {
  return (
    <div className="conf-fatura-detail-section">
      <div className="conf-fatura-detail-section__head">
        <div>
          <div className="conf-fatura-detail-section__title">{title}</div>
          {description ? (
            <div className="conf-fatura-detail-section__description">
              {description}
            </div>
          ) : null}
        </div>
      </div>
      <Row>{children}</Row>
    </div>
  );
}

function Campo({ label, valor, md = 4 }) {
  return (
    <Col md={md} className="conf-fatura-detail-field-col">
      <div className="conf-fatura-detail-field">
        <div className="conf-fatura-detail-field__label">{label}</div>
        <div className="conf-fatura-detail-field__value">{valorCampo(valor)}</div>
      </div>
    </Col>
  );
}

// Tabela read-only de CT-es da fatura (aba "Fretes" do detalhe).
function FretesTabela({ ctes }) {
  return (
    <div className="table-responsive">
      <Table className="conf-fatura-table" hover>
        <thead className="text-primary">
          <tr>
            <th>Nº documento</th>
            <th>Série</th>
            <th>Modal</th>
            <th>Emissão</th>
            <th className="num">Frete Cobrado</th>
            <th className="num">Frete Simulado</th>
            <th>Diverg.</th>
            <th>Situação</th>
            <th className="num">Qt. NF</th>
          </tr>
        </thead>
        <tbody>
          {ctes.map((c) => (
            <tr key={c.id}>
              <td>{c.numero}</td>
              <td>{c.serie}</td>
              <td>{c.modal}</td>
              <td>{fmtData(c.dataEmissao)}</td>
              <td className="num">{fmtBRL(c.freteCobrado)}</td>
              <td className="num">{fmtBRL(c.freteSimulado)}</td>
              <td>
                {c.divergencia ? (
                  <span className="conf-fatura-diverg-sim">Sim</span>
                ) : (
                  "Não"
                )}
              </td>
              <td>{c.situacaoNome}</td>
              <td className="num">{fmtInt(c.qtdNf)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

const DETALHE_TABS = [
  ["geral", "Geral"],
  ["fretes", "Fretes"],
  ["remetente", "Remetente"],
  ["transportadora", "Transportadora"],
  ["lancamentos", "Lançamentos"],
];

export function FaturaDetalheModal({ isOpen, onClose, fatura }) {
  const id = fatura && fatura.id;
  const [tab, setTab] = useState("geral");
  const [det, setDet] = useState(null);
  const [loadingDet, setLoadingDet] = useState(false);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    if (!isOpen || !id) {
      setTab("geral");
      setDetailError(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingDet(true);
    setDet(null);
    setDetailError(null);
    api
      .get(`/faturas/conferencia/${id}/detalhe`)
      .then((r) => {
        if (!cancelled) setDet(r.data);
      })
      .catch((e) => {
        if (!cancelled) {
          setDet(null);
          setDetailError(
            (e && e.response && e.response.data && e.response.data.error) ||
              e.message ||
              "Não foi possível carregar o detalhe completo."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDet(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, id]);

  // Carrega CT-es / lançamentos só quando a aba correspondente está ativa.
  const ctes = useDrilldown(
    isOpen && tab === "fretes",
    id ? `/faturas/conferencia/${id}/ctes` : null
  );
  const lanc = useDrilldown(
    isOpen && tab === "lancamentos",
    id ? `/faturas/conferencia/${id}/lancamentos` : null
  );

  if (!fatura) return null;
  const d = det || fatura; // fallback pros dados da linha enquanto carrega

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      titulo={`Fatura ${fatura.id}`}
      subtitulo={d.transportadoraFantasia}
      variant="detail"
    >
      <div className="conf-fatura-detail">
        <div className="conf-fatura-detail-hero">
          <div className="conf-fatura-detail-hero__main">
            <div className="conf-fatura-detail-hero__eyebrow">Detalhe completo</div>
            <div className="conf-fatura-detail-hero__title">
              Fatura {d.numFaturaExterna || d.id}
            </div>
            <div className="conf-fatura-detail-hero__subtitle">
              Interno {d.id} · {d.transportadoraFantasia || "Transportadora não informada"}
            </div>
          </div>
          <div className="conf-fatura-detail-hero__status">
            <span>{d.situacaoNome || "-"}</span>
            {loadingDet ? <Spinner size="sm" color="info" /> : null}
          </div>
        </div>

        <div className="conf-fatura-detail-metrics">
          <DetailMetric label="Total da fatura" value={fmtBRL(d.valTotalFatura)} tone="primary" />
          <DetailMetric label="Frete cobrado" value={fmtBRL(d.valTotalFrete)} />
          <DetailMetric label="Documentos" value={fmtInt(d.qtdCte)} />
          <DetailMetric
            label="Divergência"
            value={d.divergencia ? "Sim" : "Não"}
            tone={d.divergencia ? "danger" : "success"}
          />
        </div>

        {detailError ? (
          <div className="conf-fatura-detail-alert">
            Usando dados resumidos da linha. {detailError}
          </div>
        ) : null}

        <div className="conf-fatura-detail-tabs" role="tablist">
          {DETALHE_TABS.map(([k, label]) => (
            <button
              key={k}
              type="button"
              className={`conf-fatura-detail-tab${tab === k ? " is-active" : ""}`}
              onClick={() => setTab(k)}
              role="tab"
              aria-selected={tab === k}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="conf-fatura-detail-content">
          {tab === "geral" && (
            <>
              <DetailSection
                title="Identificação"
                description="Dados principais usados para localizar e acompanhar a fatura."
              >
                <Campo label="Nr. Interno" valor={d.id} />
                <Campo label="Nr. Externo" valor={d.numFaturaExterna} />
                <Campo label="Situação" valor={d.situacaoNome} />
                <Campo label="Momento" valor={d.momentoFatura} />
                <Campo label="Gerado pelo(a)" valor={d.geradoPor} />
                <Campo label="Divergência" valor={d.divergencia ? "Sim" : "Não"} />
              </DetailSection>

              <DetailSection title="Valores" description="Resumo financeiro consolidado.">
                <Campo label="Vl. Frete (Simulado)" valor={fmtBRL(d.valTotalFreteSimulado)} />
                <Campo label="Vl. Frete (Cobrado)" valor={fmtBRL(d.valTotalFrete)} />
                <Campo label="Vl. Frete (Combinado)" valor={fmtBRL(d.valTotalFreteCombinado)} />
                <Campo label="Total Débito" valor={fmtBRL(d.valTotalDebito)} />
                <Campo label="Total Crédito" valor={fmtBRL(d.valTotalCredito)} />
                <Campo label="Total da Fatura" valor={fmtBRL(d.valTotalFatura)} />
              </DetailSection>

              <DetailSection title="Documentos" description="Contadores de validação vinculados à fatura.">
                <Campo label="Qt. documentos" valor={fmtInt(d.qtdCte)} md={6} />
                <Campo
                  label="Documentos OK / NOK"
                  valor={`${fmtInt(d.qtdCteOk)} / ${fmtInt(d.qtdCteNok)}`}
                  md={6}
                />
              </DetailSection>

              <DetailSection title="Datas" description="Datas operacionais e fiscais da fatura.">
                <Campo label="Dt. Emissão Int." valor={fmtDataHora(d.dataEmissaoInterna)} />
                <Campo label="Dt. Emissão Ext." valor={fmtDataPura(d.dataEmissaoExterna)} />
                <Campo label="Dt. Venc. Prev." valor={fmtDataPura(d.dataVencimentoPrevista)} />
                <Campo label="Dt. Venc. Ext." valor={fmtDataPura(d.dataVencimentoExterna)} />
                <Campo label="Dt. Integração" valor={fmtDataBRT(d.dataPagamento)} />
                <Campo label="Dt. Pagamento" valor={fmtDataBRT(d.dataPagamentoReal)} />
                <Campo label="Comp. De" valor={fmtDataPura(d.competenciaDe)} />
                <Campo label="Comp. Até" valor={fmtDataPura(d.competenciaAte)} />
              </DetailSection>
            </>
          )}

          {tab === "fretes" && (
            <DetailSection title="Fretes" description="CT-es vinculados à fatura e comparativo de valores.">
              <Col md={12}>
                <EstadoModal
                  loading={ctes.loading}
                  error={ctes.error}
                  vazio={ctes.data.length === 0}
                />
                {!ctes.loading && !ctes.error && ctes.data.length > 0 && (
                  <FretesTabela ctes={ctes.data} />
                )}
              </Col>
            </DetailSection>
          )}

          {tab === "remetente" && (
            <DetailSection title="Remetente" description="Dados cadastrais do remetente vinculado.">
              <Campo label="CNPJ / CPF" valor={d.remetenteCnpj} />
              <Campo label="Fantasia" valor={d.remetenteFantasia} />
              <Campo label="Cidade" valor={d.remetenteCidade} />
              <Campo label="UF" valor={d.remetenteUf} />
            </DetailSection>
          )}

          {tab === "transportadora" && (
            <DetailSection
              title="Transportadora"
              description="Dados cadastrais da transportadora vinculada à fatura."
            >
              <Campo label="CNPJ / CPF" valor={d.transportadoraCnpj} />
              <Campo label="Razão Social" valor={d.transportadoraRazao} />
              <Campo label="Fantasia" valor={d.transportadoraFantasia} />
              <Campo label="Cidade" valor={d.transportadoraCidade} />
              <Campo label="UF" valor={d.transportadoraUf} />
            </DetailSection>
          )}

          {tab === "lancamentos" && (
            <DetailSection title="Lançamentos" description="Débitos e créditos vinculados à fatura.">
              <Col md={12}>
                <EstadoModal
                  loading={lanc.loading}
                  error={lanc.error}
                  vazio={lanc.data.length === 0}
                />
                {!lanc.loading && !lanc.error && lanc.data.length > 0 && (
                  <div className="table-responsive">
                    <Table className="conf-fatura-table" hover>
                      <thead className="text-primary">
                        <tr>
                          <th>Tipo</th>
                          <th className="num">Valor</th>
                          <th>Descrição</th>
                          <th>Nota Fiscal</th>
                          <th>Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lanc.data.map((l) => (
                          <tr key={l.id}>
                            <td>
                              {l.tipo === "D" ? "Débito" : l.tipo === "C" ? "Crédito" : l.tipo}
                            </td>
                            <td className="num">{fmtBRL(l.valor)}</td>
                            <td>{l.descricao}</td>
                            <td>{l.notaFiscalNumero}</td>
                            <td>{fmtDataHora(l.data)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Col>
            </DetailSection>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

// Conciliação (PO1-3191) — um modal só, montado pelas flags da ação em
// ACOES_CONCILIACAO: motivo, texto, anexos, responsável, CT-e em questão e
// data de integração. Com `faturas` (lista) roda em modo lote (só APROVAR),
// via POST /faturas/conferencia/lote.
const msgErro = (e, fallback) =>
  (e && e.response && e.response.data && e.response.data.error) ||
  (e && e.message) ||
  fallback;

function SeletorResponsavel({ idFatura, valor, onChange, disabled }) {
  const [opcoes, setOpcoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!idFatura) return undefined;
    let cancelado = false;
    setLoading(true);
    setErro(null);
    api
      .get(`/faturas/conferencia/${idFatura}/responsaveis`)
      .then((res) => {
        if (cancelado) return;
        const lista = Array.isArray(res.data) ? res.data : [];
        setOpcoes(lista.map((r) => ({ value: r.id, label: labelResponsavel(r) })));
      })
      .catch((e) => {
        if (!cancelado) setErro(msgErro(e, "Não foi possível carregar os responsáveis."));
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [idFatura]);

  return (
    <FormGroup>
      <Label>Responsável *</Label>
      <Select
        classNamePrefix="react-select"
        styles={selectNoModalStyles}
        {...reactSelectPortalProps()}
        options={opcoes}
        value={opcoes.find((o) => o.value === valor) || null}
        onChange={(opt) => onChange(opt ? opt.value : null)}
        isLoading={loading}
        isDisabled={disabled}
        placeholder="Escolha quem vai cuidar da fatura"
        noOptionsMessage={() => "Nenhum usuário disponível"}
      />
      {erro && <small className="conf-fatura-conc-erro">{erro}</small>}
    </FormGroup>
  );
}

function SeletorCtes({ idFatura, selecionados, onChange, disabled }) {
  const url = idFatura ? `/faturas/conferencia/${idFatura}/ctes` : null;
  const { loading, error, data } = useDrilldown(Boolean(url), url);
  const iniciado = useRef(false);

  // Pré-marca os CT-e já marcados NOK (os "CT-e em questão" da D2).
  useEffect(() => {
    if (iniciado.current || !data.length) return;
    iniciado.current = true;
    onChange(data.filter((c) => c.situacaoCodigo === "NOK").map((c) => c.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const alternar = (id) => {
    const set = new Set(selecionados);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange(Array.from(set));
  };

  return (
    <FormGroup>
      <Label>Documentos em questão ({fmtInt(selecionados.length)} marcado(s) como não OK)</Label>
      <EstadoModal loading={loading} error={error} vazio={!loading && !error && data.length === 0} />
      {!loading && !error && data.length > 0 && (
        <div className="conf-fatura-conc-ctes">
          {data.map((c) => (
            <label key={c.id} className="conf-fatura-conc-cte">
              <input
                type="checkbox"
                checked={selecionados.includes(c.id)}
                onChange={() => alternar(c.id)}
                disabled={disabled}
              />
              <span>
                {rotuloDocumento(c.modelo)} {c.numero}
                {c.serie !== undefined && c.serie !== null ? `/${c.serie}` : ""}
              </span>
              <span className="conf-fatura-conc-cte__valor">{fmtBRL(c.freteCobrado)}</span>
              {c.divergencia ? <span className="conf-fatura-diverg-sim">Divergente</span> : null}
            </label>
          ))}
        </div>
      )}
    </FormGroup>
  );
}

const camposVazios = {
  dataPagamento: "",
  observacao: "",
  motivo: "",
  idResponsavel: null,
  idsCte: [],
};

// `observacaoInicial` e `anexosIniciais` (opcionais): a Auditoria do Pedido
// (PO1-3243) abre o lote já com a observação citando o pedido e, ao devolver,
// com o PDF da auditoria anexado — o usuário pode editar ou remover.
export function ConciliacaoModal({
  isOpen,
  onClose,
  fatura,
  faturas,
  acao,
  onDone,
  observacaoInicial,
  anexosIniciais,
}) {
  const [campos, setCampos] = useState(camposVazios);
  const [arquivos, setArquivos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setCampos({ ...camposVazios, observacao: observacaoInicial || "" });
      setArquivos(anexosIniciais || []);
      setError(null);
    }
  }, [isOpen, acao, observacaoInicial, anexosIniciais]);

  const lote = Array.isArray(faturas) && faturas.length > 0;
  if (!acao || (!fatura && !lote)) return null;
  const def = ACOES_CONCILIACAO[acao] || { label: acao };
  const corBotao = ["success", "danger", "info", "warning"].includes(def.tom) ? def.tom : "primary";
  const setCampo = (campo, valor) => setCampos((prev) => ({ ...prev, [campo]: valor }));
  const textoObrigatorio = !!(def.exigeTexto || def.exigeMotivo);
  const titulo = lote ? `${def.label} · ${fmtInt(faturas.length)} fatura(s)` : rotuloAcao(acao, fatura.situacaoCodigo, fatura);

  const handleConfirmar = async () => {
    const erroValidacao = validarConciliacao(acao, campos);
    if (erroValidacao) {
      setError(erroValidacao);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (lote) {
        const form = montarCorpoLote(acao, faturas.map((f) => f.id), campos, arquivos);
        const res = await api.post("/faturas/conferencia/lote", form, {
          headers: { "content-type": "multipart/form-data" },
        });
        if (onDone) onDone((res.data && res.data.resultados) || [], acao);
      } else {
        const { multipart, body } = montarCorpoConciliacao(acao, campos, arquivos);
        const res = await api.post(
          `/faturas/conferencia/${fatura.id}/conciliar`,
          body,
          multipart ? { headers: { "content-type": "multipart/form-data" } } : undefined
        );
        if (onDone) onDone(res && res.data, acao);
      }
      onClose();
    } catch (e) {
      setError(msgErro(e, "Erro ao executar a ação"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      titulo={titulo}
      subtitulo={
        lote
          ? faturas.map((f) => f.numFaturaExterna || f.id).join(", ")
          : `Fatura ${fatura.numFaturaExterna || fatura.id} · ${fatura.transportadoraFantasia || ""}`
      }
    >
      {lote ? (
        <p>
          Confirma <strong>{def.label}</strong> em <strong>{fmtInt(faturas.length)}</strong>{" "}
          fatura(s)? O resultado sai por fatura: uma falha não desfaz as outras.
        </p>
      ) : (
        <p>
          Confirma <strong>{rotuloAcao(acao, fatura.situacaoCodigo, fatura)}</strong> na fatura{" "}
          <strong>{fatura.numFaturaExterna || fatura.id}</strong> (situação atual:{" "}
          {labelSituacao(fatura.situacaoCodigo, fatura.situacaoNome)})?
        </p>
      )}
      {def.descricao && <p className="conf-fatura-conc-descricao">{def.descricao}</p>}
      {acao === "PAGAR" &&
        (lote ? faturas.some(enviaFaturaPorEmail) : enviaFaturaPorEmail(fatura)) && (
          <p className="conf-fatura-conc-descricao">
            {lote ? "Nas faturas de embarcador que recebe por e-mail, o" : "O"} e-mail com o PDF da fatura e os XML
            dos CT-e vai agora para o financeiro do embarcador. Se o envio falhar, a fatura continua Liberada.
          </p>
        )}

      {def.exigeDataPagamento && (
        <FormGroup>
          <Label>
            {acao === "MARCAR_PAGA"
              ? "Data do pagamento *"
              : !lote && enviaFaturaPorEmail(fatura)
              ? "Data de envio ao financeiro *"
              : "Data de Integração *"}
          </Label>
          <Input
            type="date"
            value={campos.dataPagamento}
            onChange={(e) => {
              const valor = e.target.value;
              setCampo("dataPagamento", valor);
            }}
            disabled={saving}
          />
        </FormGroup>
      )}

      {def.exigeMotivo && (
        <FormGroup>
          <Label>Motivo *</Label>
          <Input
            type="select"
            name="motivo"
            value={campos.motivo}
            onChange={(e) => {
              const valor = e.target.value;
              setCampo("motivo", valor);
            }}
            disabled={saving}
          >
            <option value="">Escolha o motivo</option>
            {MOTIVOS_APROVACAO.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </Input>
        </FormGroup>
      )}

      {def.exigeResponsavel && (
        <SeletorResponsavel
          // Em lote, a lista vem da primeira fatura; o backend confere o
          // responsável em cada uma (falha só na que não aceitar).
          idFatura={lote ? faturas[0].id : fatura.id}
          valor={campos.idResponsavel}
          onChange={(v) => setCampo("idResponsavel", v)}
          disabled={saving}
        />
      )}

      <FormGroup>
        <Label>
          {acao === "TRATATIVA"
            ? "Nota para o responsável *"
            : textoObrigatorio
            ? "Justificativa *"
            : "Observação"}
        </Label>
        <Input
          type="textarea"
          name="observacao"
          rows={3}
          value={campos.observacao}
          onChange={(e) => {
            const valor = e.target.value;
            setCampo("observacao", valor);
          }}
          disabled={saving}
          maxLength={OBSERVACAO_MAX}
          placeholder={textoObrigatorio ? "Obrigatório." : undefined}
        />
      </FormGroup>

      {def.selecionaCtes && lote && (
        <p className="conf-fatura-conc-descricao">
          Em lote, cada fatura vai com os CT-e que já estão marcados como Não OK.
        </p>
      )}

      {def.selecionaCtes && !lote && (
        <SeletorCtes
          idFatura={fatura.id}
          selecionados={campos.idsCte}
          onChange={(ids) => setCampo("idsCte", ids)}
          disabled={saving}
        />
      )}

      {def.permiteAnexo && (
        <AnexosCampo arquivos={arquivos} onChange={setArquivos} onErro={setError} disabled={saving} />
      )}

      {error && (
        <div className="conf-fatura-diverg-sim" style={{ marginBottom: 8 }} role="alert">
          {typeof error === "object" ? JSON.stringify(error) : error}
        </div>
      )}

      <div className="conf-fatura-pagination">
        <Button color="default" outline onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button color={corBotao} onClick={handleConfirmar} disabled={saving} className="conf-fatura-conc-confirmar">
          {saving ? <Spinner size="sm" /> : "Confirmar"}
        </Button>
      </div>
    </ModalShell>
  );
}

const toDateInput = (v) => {
  const d = dataPura(v);
  return d ? d.format("YYYY-MM-DD") : "";
};

// Editar campos externos da fatura (só faturas em "A Validar").
export function EditarFaturaModal({ isOpen, onClose, fatura, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && fatura) {
      setForm({
        numFaturaExterna: fatura.numFaturaExterna || "",
        datEmissaoExterna: toDateInput(fatura.dataEmissaoExterna),
        datVencimentoExterna: toDateInput(fatura.dataVencimentoExterna),
        datPagamento: toDateInput(fatura.dataPagamento),
        valTotalFatura: fatura.valTotalFatura ?? "",
      });
      setError(null);
    }
  }, [isOpen, fatura]);

  if (!fatura) return null;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSalvar = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/faturas/conferencia/${fatura.id}`, {
        numFaturaExterna: form.numFaturaExterna || null,
        datEmissaoExterna: form.datEmissaoExterna || null,
        datVencimentoExterna: form.datVencimentoExterna || null,
        datPagamento: form.datPagamento || null,
        valTotalFatura: form.valTotalFatura === "" ? null : Number(form.valTotalFatura),
      });
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      setError(
        (e && e.response && e.response.data && e.response.data.error) ||
          e.message ||
          "Erro ao salvar"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      titulo={`Editar Fatura ${fatura.id}`}
      subtitulo={fatura.transportadoraFantasia}
    >
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>Nr. Fatura Externa</Label>
            <Input
              value={form.numFaturaExterna || ""}
              onChange={(e) => set("numFaturaExterna", e.target.value)}
              disabled={saving}
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label>Valor Total da Fatura</Label>
            <Input
              type="number"
              step="0.01"
              value={form.valTotalFatura}
              onChange={(e) => set("valTotalFatura", e.target.value)}
              disabled={saving}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>Dt. Emissão Externa</Label>
            <Input
              type="date"
              value={form.datEmissaoExterna || ""}
              onChange={(e) => set("datEmissaoExterna", e.target.value)}
              disabled={saving}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>Dt. Vencimento Externa</Label>
            <Input
              type="date"
              value={form.datVencimentoExterna || ""}
              onChange={(e) => set("datVencimentoExterna", e.target.value)}
              disabled={saving}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>Dt. Integração</Label>
            <Input
              type="date"
              value={form.datPagamento || ""}
              onChange={(e) => set("datPagamento", e.target.value)}
              disabled={saving}
            />
          </FormGroup>
        </Col>
      </Row>

      {error && (
        <div className="conf-fatura-diverg-sim" style={{ marginBottom: 8 }}>
          {typeof error === "object" ? JSON.stringify(error) : error}
        </div>
      )}

      <div className="conf-fatura-pagination">
        <Button color="default" outline onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button color="primary" onClick={handleSalvar} disabled={saving}>
          {saving ? <Spinner size="sm" /> : "Salvar"}
        </Button>
      </div>
    </ModalShell>
  );
}

// Histórico de conciliações (aberto pelo link de Situação).
export function ConciliacoesModal({ isOpen, onClose, fatura }) {
  const id = fatura && fatura.id;
  const url = isOpen && id ? `/faturas/conferencia/${id}/conciliacoes` : null;
  const { loading, error, data } = useDrilldown(isOpen, url);

  // Download autenticado do comprovante (api carrega o Bearer; <a href> não).
  const baixarAnexo = async (anexo) => {
    try {
      const resp = await api.get(
        `/faturas/conferencia/${id}/anexo/${anexo.id}`,
        { responseType: "blob" }
      );
      const objUrl = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = anexo.nome || `anexo.${anexo.formato || "bin"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objUrl);
    } catch (e) {
      /* silencioso — o histórico permanece aberto */
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      titulo="Histórico de Conciliação"
      subtitulo={
        fatura ? (
          <span className="conf-hist-subtitulo">
            <span>
              Fatura {fatura.numFaturaExterna || fatura.id}
              {fatura.transportadoraFantasia ? ` · ${fatura.transportadoraFantasia}` : ""}
            </span>
            {fatura.situacaoCodigo && (
              <StatusBadge codigo={fatura.situacaoCodigo}>
                {labelSituacao(fatura.situacaoCodigo, fatura.situacaoNome)}
              </StatusBadge>
            )}
            {fatura.numFaturaExterna && (
              <span className="conf-hist-interno">interno {fatura.id}</span>
            )}
          </span>
        ) : (
          ""
        )
      }
    >
      <EstadoModal loading={loading} error={error} vazio={data.length === 0} />
      {!loading && !error && data.length > 0 && (
        <LinhaDoTempoConciliacao
          grupos={montarLinhaDoTempo(data, {
            codigo: fatura && fatura.situacaoCodigo,
            nome: fatura && fatura.situacaoNome,
          })}
          onBaixarAnexo={baixarAnexo}
        />
      )}
    </ModalShell>
  );
}

// Confirmação de exclusão de fatura (destrutivo).
export function DeletarFaturaModal({ isOpen, onClose, fatura, onDone }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) setError(null);
  }, [isOpen]);

  if (!fatura) return null;

  const handleConfirmar = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.delete(`/faturas/conferencia/${fatura.id}`);
      if (onDone) onDone();
      onClose();
    } catch (e) {
      setError(
        (e && e.response && e.response.data && e.response.data.error) ||
          e.message ||
          "Erro ao excluir a fatura"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      titulo={`Excluir Fatura ${fatura.id}`}
      subtitulo={fatura.transportadoraFantasia}
    >
      <p>
        Tem certeza que deseja <strong>excluir</strong> a fatura{" "}
        <strong>{fatura.id}</strong>? Esta ação é <strong>irreversível</strong>.
      </p>
      <p style={{ opacity: 0.8 }}>
        Os CT-es vinculados voltam para “A Faturar”. Faturas com vínculos
        (conciliação, lançamentos ou alertas) não podem ser excluídas.
      </p>

      {error && (
        <div className="conf-fatura-diverg-sim" style={{ marginBottom: 8 }}>
          {typeof error === "object" ? JSON.stringify(error) : error}
        </div>
      )}

      <div className="conf-fatura-pagination">
        <Button color="default" outline onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button color="danger" onClick={handleConfirmar} disabled={saving}>
          {saving ? <Spinner size="sm" /> : "Excluir"}
        </Button>
      </div>
    </ModalShell>
  );
}
